import { useCallback } from "react";

import { IModal, ModalComponent } from "@customer-app/account/views/OperationAquarius/GlobalModal";
import { CustomerAction } from "@customer-app/state/typedCustomerRedux";

import { useSmartrrCustomerDispatch, useSmartrrCustomerSelector } from "../../typedCustomerReduxHooks";

// TODO: Future refactor: move all hooks to useModalActions (open(),close(),update())

export interface IModalOptions {
  contained?: boolean;
}

/**
 * Action creator for opening a modal with a defined component, props, and options.
 */
export const openModal = <S extends ModalComponent>(
  component: S,
  props?: IModal[S],
  options?: IModalOptions
): CustomerAction<"OPEN_MODAL"> => ({
  type: "OPEN_MODAL",
  payload: {
    component,
    props,
    options,
  },
});

/**
 * Hook for opening a modal with a defined component, props, and options.
 */
export const useOpenModal = () => {
  const dispatch = useSmartrrCustomerDispatch();
  return useCallback(
    <S extends ModalComponent>(component: S, props?: IModal[S], options?: IModalOptions) =>
      dispatch(openModal(component, props, options)),
    []
  );
};

/**
 * Custom hook for closing the modal.
 * Uses `useToggleModal` to retain current modal props and only updates modal component to `CLOSE_MODAL`.
 * Retention of props prevents undefined errors within `<GlobalModal>` during closing
 */
export const useCloseModal = () => {
  const updateModal = useUpdateModalProps();
  return useCallback(() => updateModal({ componentOverride: "CLOSE_MODAL" }), [updateModal]);
};

/**
 * Hook for toggling a modal using IModal.
 */
export const useToggleModal = () => {
  const activeComponent = useSmartrrCustomerSelector(state => state.modal.activeComponent as ModalComponent);

  const closeModalFunc = useCloseModal();
  const openModalFunc = useOpenModal();
  const updateModalFunc = useUpdateModalProps();
  return useCallback(
    <M extends ModalComponent>(modalType: M, props: IModal[M], close?: boolean, options?: IModalOptions) => {
      if (close) {
        closeModalFunc();
      } else if (activeComponent && activeComponent !== "CLOSE_MODAL") {
        updateModalFunc({ componentOverride: modalType, propOverrides: props, optionOverrides: options });
      } else {
        openModalFunc(modalType, props, options);
      }
    },
    []
  );
};

/**
 * Hook for updating individual modal parameters without re-providing all previously provided parameters.
 */
export const useUpdateModalProps = () => {
  const { activeComponentProps, activeComponent, options } = useSmartrrCustomerSelector(state => state.modal);
  const dispatch = useSmartrrCustomerDispatch();

  return useCallback(
    <T extends ModalComponent>({
      componentOverride = activeComponent,
      optionOverrides,
      propOverrides,
    }: {
      componentOverride?: T;
      propOverrides?: Partial<IModal[T]>;
      optionOverrides?: Partial<IModalOptions>;
    }) => {
      if (activeComponentProps) {
        dispatch(
          openModal(
            componentOverride,
            { ...activeComponentProps, ...propOverrides },
            {
              ...options,
              ...optionOverrides,
            }
          )
        );
      }
    },
    [activeComponentProps]
  );
};
